









































































import type { Ref } from '@vue/composition-api';
import { watch, defineComponent, ref } from '@vue/composition-api';
import Animation from '@getaccept/lib-shared/src/components/Animation.vue';
import { sanitizeEmail, validateEmail } from '@getaccept/lib-shared/src/helpers';
import { RecaptchaAction } from '@getaccept/lib-shared/src/recaptcha/recaptcha-action';
import type { AxiosError } from 'axios';
import bugsnagClient from '@getaccept/lib-shared/src/bugsnag';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import { ResetRequestError } from '../types/enums/reset-request-error';
import type { ErrorMessage } from '../../types/error-message';
import { RecaptchaService } from '../../api/recaptcha/recaptcha.service';
import { LoginService } from '../../api/login/login.service';
import type { RequestResponse } from '../../api/login/types/request-response';
import { getErrorMessage } from '../../helpers/error-message';
import { ErrorKey } from '../../types/enums/error-key';
import router from '../../router/router';

export default defineComponent({
  components: {
    Animation,
  },
  setup() {
    const email = ref('');
    const emailError = ref('');
    const error: Ref<ErrorMessage | null> = ref(null);
    const loading = ref(false);
    const requestSent = ref(false);

    watch(email, () => {
      emailError.value = '';
    });

    const resetRequest = async (email: string) => {
      loading.value = true;
      try {
        const recaptchaToken: string = await RecaptchaService.getToken(RecaptchaAction.Request);
        const data: RequestResponse = await LoginService.request({ email, recaptchaToken });
        if (data.status === 0) {
          resetRequestFailed({ data });
          loading.value = false;
          return;
        }
        resetRequestSuccess();
        loading.value = false;
      } catch (error) {
        resetRequestFailed({ requestError: error });
        loading.value = false;
      }
    };

    const resetRequestFailed = ({
      data,
      requestError,
    }: {
      data?: RequestResponse;
      requestError?: AxiosError;
    }) => {
      const errorMessage: string = data?.message;
      let newError: ErrorMessage;
      switch (errorMessage) {
        case ResetRequestError.Throttled:
          newError = getErrorMessage(ErrorKey.Throttled);
          break;
        case ResetRequestError.ErrorSendingRequest:
        default:
          newError = getErrorMessage(ErrorKey.Unknown);
          if (bugsnagClient) {
            if (requestError) {
              bugsnagClient.notify(requestError, event => {
                event.groupingHash = requestError;
              });
            } else {
              bugsnagClient.notify(new Error(JSON.stringify(data)), event => {
                event.groupingHash = JSON.stringify(data);
              });
            }
          }
      }
      error.value = newError;
    };
    const resetRequestSuccess = () => {
      requestSent.value = true;
    };

    const validateEmailAddress = (email: string): string => {
      if (!email) {
        return t('email-is-required');
      } else if (!validateEmail(email)) {
        return t('enter-a-valid-email');
      }
      return '';
    };

    const handleSubmit = () => {
      const cleanEmail = sanitizeEmail(email.value);
      emailError.value = validateEmailAddress(cleanEmail);
      if (emailError.value) {
        return;
      }
      resetRequest(cleanEmail);
    };

    const handleLoginClick = () => {
      router.push({ name: 'login' });
    };

    return {
      email,
      emailError,
      error,
      loading,
      requestSent,
      handleSubmit,
      handleLoginClick,
      resetRequest,
      validateEmailAddress,
    };
  },
});
